import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Intention ponctuelle transmise à une page liste à son ouverture (raccourci
 * « Enregistrer une œuvre » → modale de création ouverte sur la liste).
 *
 * Elle passe par le state de navigation et non par un query param : les
 * listes réécrivent leurs query params (pagination, filtres) à partir de
 * valeurs périmées, ce qui remettait un `?action=` déjà consommé dans l'URL.
 */
export type NavigationAction = "new" | "advanced-search";

const STATE_KEY = "action";

export function navigationActionState(action: NavigationAction) {
  return { [STATE_KEY]: action };
}

/**
 * `true` si la page a été ouverte avec `action`. Le state est ensuite effacé
 * pour qu'un rechargement ou un retour arrière ne rouvre pas la modale.
 */
export function useConsumeNavigationAction(action: NavigationAction): boolean {
  const location = useLocation();
  const navigate = useNavigate();
  const [requested] = useState(
    () =>
      (location.state as Record<string, unknown> | null)?.[STATE_KEY] ===
      action,
  );

  useEffect(() => {
    if (!requested) return;
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
    // Une seule fois, à l'arrivée sur la page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return requested;
}
